import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  IconButton,
  Select,
  type SelectOption,
} from '@civilgrid/ui/components';
import { getNonNullUserName } from '@civilgrid/ui/utils';
import { keyBy } from 'lodash';
import { useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import { editMember } from '~/api/organizations';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Member, OrganizationDetail } from '~/common/types';
import Notify from '~/components2/Notify';
import rootStore from '~/store';
import RolePermissions from './RolePermissions';

interface Props {
  member: Member;
  onSave: () => void;
}

export default function EditUserDetails({ member, onSave }: Props) {
  const { currentOrganizationDetail, myRole } = useSnapshot(rootStore) as typeof rootStore;

  const [selectedRole, setSelectedRole] = useState<string>(member.role);
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmRemoveOpen, setIsConfirmRemoveOpen] = useState(false);
  const removeTriggerRef = useRef<HTMLButtonElement>(null);

  function getRoleName(role: string) {
    if (currentOrganizationDetail.isLoading) {
      return role;
    } else {
      return keyBy(currentOrganizationDetail.roles, 'roleId')[role].name;
    }
  }

  function getRoles(): SelectOption[] {
    if (currentOrganizationDetail.isLoading) {
      return [];
    } else {
      const items: SelectOption[] = [];
      let reachedMyLevel = false;
      currentOrganizationDetail.roles.forEach((r) => {
        if (!reachedMyLevel && r.roleId === myRole!.roleId) {
          reachedMyLevel = true;
        }
        if (reachedMyLevel) {
          items.push({
            label: r.name,
            value: r.roleId,
          });
        }
      });

      return items;
    }
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <IconButton
            variant="secondary"
            icon="gear"
            size="icon"
            aria-label={`Edit ${getNonNullUserName(member.user.name) || member.user.email}`}
            data-testid="org-member-edit-trigger"
          />
        </DialogTrigger>
        <DialogContent
          title="Edit User"
          className="z-[110] w-auto min-w-min max-w-[75vw]"
          overlayClassName="z-[100]"
          data-testid="org-member-edit-dialog"
        >
          <DialogBody className="text-sm">
            <div className="min-w-[40vw]">
              <div className="flex justify-between">
                <div>
                  <div className="text-lg font-semibold">{member.user.name}</div>
                  <div className="text-sm italic">{member.user.email}</div>

                  {member.role !== selectedRole && <div>Current Role: {getRoleName(member.role)}</div>}
                  <div className="mt-4">
                    <Can action={Action.UpdateOrgRoles}>
                      <Select
                        label="Role"
                        onValueChange={(value) => value && setSelectedRole(value)}
                        options={getRoles()}
                        value={selectedRole}
                        data-testid="org-member-edit-role-select"
                      />
                    </Can>
                  </div>
                </div>
                <div>
                  <Can action={Action.RemoveOrgMember}>
                    <Button
                      ref={removeTriggerRef}
                      variant="danger"
                      aria-haspopup="dialog"
                      onClick={() => setIsConfirmRemoveOpen(true)}
                      data-testid="org-member-remove-trigger"
                    >
                      Remove from org
                    </Button>
                  </Can>
                </div>
              </div>
              <RolePermissions role={selectedRole} />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsOpen(false)} data-testid="org-member-edit-cancel">
              Cancel
            </Button>
            <Button
              data-testid="org-member-edit-save"
              onClick={async () => {
                await editMember((currentOrganizationDetail as OrganizationDetail).id, member.user.id, {
                  role: selectedRole,
                });

                Notify.info({
                  title: 'Role Updated',
                  message: `${member.user.name} has been updated`,
                });

                capturePosthogEvent(POSTHOG_EVENTS.organizationRoleUpdated, {
                  organization_id: (currentOrganizationDetail as OrganizationDetail).id,
                  member_user_id: member.user.id,
                  email: member.user.email,
                  role: selectedRole,
                });

                setIsOpen(false);
                onSave();
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isConfirmRemoveOpen} onOpenChange={setIsConfirmRemoveOpen}>
        <DialogContent
          title="Confirm"
          onCloseAutoFocus={(e) => {
            // The opener may not hold focus after a click (Safari), so return focus to it explicitly.
            const trigger = removeTriggerRef.current;
            if (!isOpen || !trigger?.isConnected) return;
            e.preventDefault();
            trigger.focus();
          }}
          className="z-[130] w-auto min-w-min max-w-[75vw]"
          overlayClassName="z-[120]"
          data-testid="org-member-remove-dialog"
        >
          <DialogBody className="text-sm">Are you sure?</DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setIsConfirmRemoveOpen(false)}
              data-testid="org-member-remove-no"
            >
              No
            </Button>
            <Button
              data-testid="org-member-remove-yes"
              onClick={async () => {
                await editMember((currentOrganizationDetail as OrganizationDetail).id, member.user.id, {
                  status: 'suspended',
                });
                Notify.info({
                  message: `${member.user.name} has been removed from the org`,
                });
                setIsConfirmRemoveOpen(false);
                setIsOpen(false);
                onSave();
              }}
            >
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
